import type { Attribut } from '@/models/interfaces/Attribut'
import { ATTRIBUT_KIND_EDITABLE } from '@/models/interfaces/Attribut'
import type { CompetenceTypeOuvrage } from '@/models/interfaces/CompetenceTypeOuvrage'
import { apiClient } from '@/plugins/axios/api'

const BASE = '/api/competence_type_ouvrages'
const ATTRIBUTS_BASE = '/api/competence_type_ouvrage_attributs'

export const CompetenceTypeOuvrageService = {
  /**
   * GET /api/competence_type_ouvrages
   * Retrieves the collection of CompetenceTypeOuvrage resources.
   * Pass expandAttributs: true to request embedded attributs (if backend supports it).
   */
  getAll(options?: { expandAttributs?: boolean }): Promise<CompetenceTypeOuvrage[]> {
    const params = options?.expandAttributs === true ? { 'groups[]': ['attributs'] } : undefined
    return apiClient
      .get(BASE, { params })
      .then((res) => (res.data['hydra:member'] as CompetenceTypeOuvrage[]) ?? [])
  },

  /**
   * GET /api/competence_type_ouvrages/{id}
   */
  getById(id: string | number): Promise<CompetenceTypeOuvrage> {
    return apiClient
      .get(`${BASE}/${encodeURIComponent(String(id))}`)
      .then((res) => res.data as CompetenceTypeOuvrage)
  },

  /**
   * GET /api/competence_type_ouvrage_attributs?typeOuvrage={code}[&competence={id}]
   * Returns attributs for the given (competence, type d'ouvrage) pair, filtered by atgKind = 'A' (editable attributes).
   * Used to display total attribute count in map popup (e.g. "1 / 3 attributs affichés").
   *
   * La compétence est facultative : une fiche ouverte par lien direct ou depuis le tableau de bord
   * ne connaît que le type d'ouvrage, et les attributs sont définis au niveau du type.
   */
  getAttributsForCompetenceAndType(
    competenceId: number | null,
    typeOuvrageCode: string,
  ): Promise<Attribut[]> {
    return apiClient
      .get(ATTRIBUTS_BASE, {
        params:
          competenceId != null
            ? { competence: competenceId, typeOuvrage: typeOuvrageCode }
            : { typeOuvrage: typeOuvrageCode },
      })
      .then((res) => {
        const data = res.data
        const list = Array.isArray(data)
          ? data
          : ((data?.['hydra:member'] as Attribut[] | undefined) ?? [])
        return list.filter((a) => a.atgKind === ATTRIBUT_KIND_EDITABLE)
      })
  },
}
